import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Button,
  List,
  Input,
  Switch,
  Skeleton,
  Typography,
  Space,
  Popconfirm,
  Tag,
  message,
} from 'antd';
import { DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons';
import useGetTemplates from './hooks/useGetTemplates';
import useSaveTemplate from './hooks/useSaveTemplate';
import useDeleteTemplate from './hooks/useDeleteTemplate';
import useCanManageSystemPrompt from '../SystemPrompt/hooks/useCanManageSysPrompt';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Template {
  id: number;
  type: string;
  name: string;
  deep: boolean;
  extra_instructions: string;
  product: string;
  updated_by: number;
  updated_by_username?: string;
  updated_at: string;
  created_at: string;
}

const TemplateListItem = ({ item, onClick, onDelete, selected }: any) => {
  return (
    <List.Item
      style={{
        cursor: 'pointer',
        backgroundColor: selected ? '#e6f7ff' : 'transparent',
        padding: '12px 16px',
        borderRadius: '4px',
      }}
      onClick={() => onClick(item)}
    >
      <div style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <Space>
            <Text strong>{item.name}</Text>
            {item.deep && (
              <Tag color="blue" icon={<ThunderboltOutlined />}>
                Deep
              </Tag>
            )}
          </Space>
          <Popconfirm
            title="Delete template"
            description="Are you sure you want to delete this template?"
            onConfirm={(e) => {
              e?.stopPropagation();
              onDelete(item.id);
            }}
            okText="Delete"
            cancelText="Cancel"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {item.type} • Updated by {item.updated_by_username || 'Unknown'} •{' '}
          {moment(item.updated_at).format('MMM D, YYYY')}
        </Text>
      </div>
    </List.Item>
  );
};

const EditForm = ({ template, onCancel, onSave, isSaving }: any) => {
  const [type, setType] = useState(template?.type || '');
  const [name, setName] = useState(template?.name || '');
  const [deep, setDeep] = useState(template?.deep || false);
  const [extraInstructions, setExtraInstructions] = useState(template?.extra_instructions || '');

  const isEditMode = !!template;

  // Reset form when template changes (switching between create/edit modes)
  React.useEffect(() => {
    setType(template?.type || '');
    setName(template?.name || '');
    setDeep(template?.deep || false);
    setExtraInstructions(template?.extra_instructions || '');
  }, [template]);

  const handleSave = () => {
    if (!type || !name || !extraInstructions) {
      message.error('All fields are required');
      return;
    }

    onSave({
      id: template?.id,
      type,
      name,
      deep,
      extra_instructions: extraInstructions,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '16px' }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>Type (Backend Key) *</Text>
            <Input
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="e.g., slice-diagnostics"
              style={{ marginTop: 4 }}
              disabled={isEditMode}
            />
            {isEditMode && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Type cannot be changed after creation
              </Text>
            )}
          </div>

          <div>
            <Text strong>Name (Display) *</Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Slice Diagnostics"
              style={{ marginTop: 4 }}
            />
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}
            >
              <Text strong>Deep Model</Text>
              <Switch checked={deep} onChange={setDeep} />
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Enable for diagnostics requiring deep analysis
            </Text>
          </div>

          <div>
            <Text strong>Extra Instructions *</Text>
            <TextArea
              value={extraInstructions}
              onChange={(e) => setExtraInstructions(e.target.value)}
              placeholder="Enter detailed instructions for this diagnostic type..."
              rows={25}
              style={{ marginTop: 4 }}
            />
          </div>
        </Space>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" onClick={handleSave} loading={isSaving}>
          Save
        </Button>
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '40px',
      textAlign: 'center',
    }}
  >
    <Text type="secondary">Select a template to edit or create a new one</Text>
  </div>
);

const DiagnosticTemplates = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const canManage = useCanManageSystemPrompt();
  const { data = [], isFetching, isError } = useGetTemplates({ open });
  const saveMutation = useSaveTemplate();
  const deleteMutation = useDeleteTemplate();

  const selectedTemplate = useMemo(() => {
    return selectedTemplateId
      ? data.find((item: Template) => item.id === selectedTemplateId)
      : null;
  }, [data, selectedTemplateId]);

  const handleClose = () => {
    setOpen(false);
    setSelectedTemplateId(null);
    setIsCreating(false);
  };

  const handleCreate = () => {
    setSelectedTemplateId(null);
    setIsCreating(true);
  };

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplateId(template.id);
    setIsCreating(false);
  };

  const handleSave = async (payload: any) => {
    await saveMutation.mutateAsync(payload);
    setSelectedTemplateId(null);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setSelectedTemplateId(null);
    setIsCreating(false);
  };

  const handleDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id);
    if (selectedTemplateId === id) {
      setSelectedTemplateId(null);
    }
  };

  if (!canManage) {
    return null;
  }

  const showForm = selectedTemplate || isCreating;

  return (
    <Drawer
      title="Diagnostic Templates"
      placement="left"
      closable={true}
      onClose={handleClose}
      open={open}
      width={900}
      extra={
        <Button type="primary" onClick={handleCreate}>
          Create
        </Button>
      }
      destroyOnClose={true}
    >
      <div style={{ display: 'flex', height: '100%', gap: 16 }}>
        {/* Left Panel - Template List */}
        <div
          style={{ flex: 1, borderRight: '1px solid #f0f0f0', paddingRight: 16, overflowY: 'auto' }}
        >
          <Title level={5} style={{ marginBottom: 16 }}>
            Templates
          </Title>
          {isFetching ? (
            <Skeleton active />
          ) : isError ? (
            <Text type="danger">Failed to load templates</Text>
          ) : (
            <List
              size="small"
              dataSource={data}
              renderItem={(item: Template) => (
                <TemplateListItem
                  item={item}
                  onClick={handleSelectTemplate}
                  onDelete={handleDelete}
                  selected={item.id === selectedTemplateId}
                />
              )}
            />
          )}
        </div>

        {/* Right Panel - Edit Form */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Title level={5} style={{ marginBottom: 16 }}>
            {isCreating
              ? 'Create Template'
              : selectedTemplate
                ? 'Edit Template'
                : 'Template Details'}
          </Title>
          {showForm ? (
            <EditForm
              template={isCreating ? null : selectedTemplate}
              onCancel={handleCancel}
              onSave={handleSave}
              isSaving={saveMutation.isLoading}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default DiagnosticTemplates;
